import { useState, type ReactElement } from 'react';
import { toast } from 'sonner';
import { MoreHorizontal, Plug, BarChart3, Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { StatusChip } from './StatusChip';
import { IntegrationDialog } from './IntegrationDialog';
import { DisconnectConfirmDialog } from './DisconnectConfirmDialog';
import {
  useDeleteIntegration,
  useTestIntegration,
  useUpsertIntegration,
  useStartGa4Oauth,
} from '@/lib/integrationsApi';
import { relativeTime } from '@/lib/relativeTime';
import type { IntegrationSummary, IntegrationType } from '@ventin/shared';

const NAMES: Record<IntegrationType, string> = {
  GHL: 'GoHighLevel',
  GA4: 'Google Analytics',
  RESEND: 'Resend',
};

const ICONS: Record<IntegrationType, ReactElement> = {
  GHL: <Plug aria-hidden="true" className="h-6 w-6" />,
  GA4: <BarChart3 aria-hidden="true" className="h-6 w-6" />,
  RESEND: <Mail aria-hidden="true" className="h-6 w-6" />,
};

export function IntegrationCard({ integration }: { integration: IntegrationSummary }) {
  const upsert = useUpsertIntegration();
  const test = useTestIntegration();
  const remove = useDeleteIntegration();
  const startGa4 = useStartGa4Oauth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isConnected = integration.status === 'CONNECTED';
  const isPending = integration.status === 'PENDING_OAUTH';

  const primaryLabel = isPending ? 'Finish setup' : isConnected ? 'Reconfigure' : 'Connect';

  async function handlePrimaryClick() {
    if (integration.type === 'GA4') {
      // BLOCKER-2: authenticated POST returning { authorizeUrl }, then client-side redirect.
      // We cannot use `window.location.href = '/api/admin/integrations/ga4/oauth/start'`
      // because a top-level navigation strips the Authorization: Bearer header.
      try {
        const { authorizeUrl } = await startGa4.mutateAsync();
        window.location.href = authorizeUrl;
      } catch {
        toast.error("Couldn't start Google OAuth — try again");
      }
      return;
    }
    setDialogOpen(true);
  }

  async function handleSubmitDialog(payload: Record<string, unknown>) {
    await upsert.mutateAsync({ type: integration.type, payload });
    toast.success('Saved');
  }

  async function handleTest() {
    try {
      const res = await test.mutateAsync(integration.type);
      if (res.test.ok) {
        toast.success('Saved');
      } else {
        toast.error(`Test connection failed: ${res.test.error ?? 'unknown error'}.`);
      }
    } catch {
      toast.error("Couldn't save — try again");
    }
  }

  async function handleDisconnect() {
    try {
      await remove.mutateAsync(integration.type);
      toast.success('Saved');
      setConfirmOpen(false);
    } catch {
      toast.error("Couldn't save — try again");
    }
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            {ICONS[integration.type]}
            <h3 className="text-base font-semibold">{NAMES[integration.type]}</h3>
          </div>
          <StatusChip status={integration.status} />
        </div>
        <p className="text-xs text-muted-foreground">
          {integration.lastCheckedAt
            ? `Last checked ${relativeTime(integration.lastCheckedAt)}`
            : 'Never checked'}
        </p>
        {integration.status === 'ERROR' && integration.lastError && (
          <Alert variant="destructive">
            <AlertDescription>{integration.lastError}</AlertDescription>
          </Alert>
        )}
        <div className="flex items-center gap-2">
          <Button onClick={handlePrimaryClick} disabled={upsert.isPending || startGa4.isPending}>
            {startGa4.isPending && integration.type === 'GA4' ? 'Redirecting…' : primaryLabel}
          </Button>
          <Button variant="ghost" onClick={handleTest} disabled={!isConnected || test.isPending}>
            {test.isPending ? 'Testing…' : 'Test connection'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={`${NAMES[integration.type]} actions`}>
                <MoreHorizontal aria-hidden="true" className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setConfirmOpen(true)}
                disabled={!isConnected && !isPending}
              >
                Disconnect
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>

      {(integration.type === 'GHL' || integration.type === 'RESEND') && (
        <IntegrationDialog
          type={integration.type}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSubmit={handleSubmitDialog}
        />
      )}

      {/* Info-8 fix: dedicated DisconnectConfirmDialog — replaces the prior stray
          <IntegrationDialog open={false}> sentinel that was bleeding into this render. */}
      <DisconnectConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        integrationName={NAMES[integration.type]}
        busy={remove.isPending}
        onConfirm={handleDisconnect}
      />
    </Card>
  );
}
